import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ShieldIcon, UploadCloudIcon, XIcon, FileIcon } from 'lucide-react';
// Bank of Maldives pinned to the top, then alphabetical
const BANKS = ['Bank of Maldives', 'Bank of Ceylon Maldives', 'Commercial Bank of Maldives', 'HDFC Bank (Maldives)', 'Habib Bank Maldives', 'HSBC Maldives', 'ICICI Bank (Maldives)', 'Maldives Islamic Bank', 'Mauritius Commercial Bank Maldives', 'State Bank of India (Malé)'];
interface SimulatedFile {
  name: string;
  size: number;
}
export function UpdatePayoutAccount() {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [bank, setBank] = useState('Bank of Maldives');
  const [accountNumber, setAccountNumber] = useState('');
  const [confirmAccountNumber, setConfirmAccountNumber] = useState('');
  const [file, setFile] = useState<SimulatedFile | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const accountsMatch = accountNumber.length > 0 && accountNumber === confirmAccountNumber;
  const showMismatch = confirmAccountNumber.length > 0 && accountNumber !== confirmAccountNumber;
  const isValid = bank !== '' && accountNumber.length >= 6 && accountsMatch && file !== null;
  const goBack = () => navigate('/settings?tab=payout');
  const simulateUpload = () => {
    const today = new Date().toISOString().slice(0, 10);
    setFile({
      name: `Bank_Statement_${today}.pdf`,
      size: 2457600
    });
  };
  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    simulateUpload();
  };
  const handleSubmit = () => {
    if (!isValid) return;
    setSubmitting(true);
    const requestId = `PAYOUT-REQ-${Date.now()}`;
    const payload = {
      id: requestId,
      bank,
      accountNumber,
      maskedAccount: `••••${accountNumber.slice(-4)}`,
      fileName: file?.name,
      submittedAt: new Date().toISOString(),
      status: 'Pending'
    };
    try {
      sessionStorage.setItem('pendingPayoutUpdate', JSON.stringify(payload));
    } catch {
      // ignore storage errors
    }
    setTimeout(() => {
      setSubmitting(false);
      navigate('/settings?tab=payout', {
        state: {
          payoutUpdateSubmitted: true,
          requestId
        }
      });
    }, 600);
  };
  return <div className="min-h-full bg-gray-50" data-id="element-10338">
      <div className="max-w-3xl mx-auto px-6 py-8 pb-32" data-id="element-10339">
        <button onClick={goBack} className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors mb-6 group" data-id="element-10340">
          <ChevronLeftIcon size={16} className="mr-1 group-hover:-translate-x-0.5 transition-transform" data-id="element-10341" />
          Back to Payout Settings
        </button>

        <h1 className="text-2xl font-bold text-gray-900 mb-4" data-id="element-10342">
          Update Payout Account (MVR)
        </h1>

        <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8" data-id="element-10343">
          <ShieldIcon size={18} className="text-blue-700 flex-shrink-0 mt-0.5" data-id="element-10344" />
          <p className="text-sm text-blue-900 leading-relaxed" data-id="element-10345">
            <span className="font-semibold" data-id="element-10346">Security Notice:</span> To protect
            your funds, updating your settlement account requires a standard
            compliance review. Your current account (Bank of Maldives ****5678)
            will continue receiving payouts until the new account is approved.
          </p>
        </div>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6 mb-6" data-id="element-10347">
          <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-10348">
            New Bank Details
          </h2>
          <p className="text-sm text-gray-500 mb-5" data-id="element-10349">
            Enter the bank account where you want to receive future payouts.
          </p>

          <div className="space-y-4" data-id="element-10350">
            <div data-id="element-10351">
              <label htmlFor="bank" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10352">
                Select your bank
              </label>
              <select id="bank" value={bank} onChange={e => setBank(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]" data-id="element-10353">
                <optgroup label="Recommended" data-id="element-10354">
                  <option value="Bank of Maldives" data-id="element-10355">Bank of Maldives</option>
                </optgroup>
                <optgroup label="All banks (A–Z)" data-id="element-10356">
                  {BANKS.filter(b => b !== 'Bank of Maldives').map(b => <option key={b} value={b} data-id="element-10357">
                      {b}
                    </option>)}
                </optgroup>
              </select>
            </div>

            <div data-id="element-10358">
              <label htmlFor="account" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10359">
                Account Number
              </label>
              <input id="account" type="text" inputMode="numeric" value={accountNumber} onChange={e => setAccountNumber(e.target.value.replace(/\D/g, ''))} onFocus={() => {
              if (accountNumber === '' && confirmAccountNumber === '') {
                setAccountNumber('7730001234567');
                setConfirmAccountNumber('7730001234567');
              }
            }} placeholder="e.g. 7730001234567" className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]" data-id="element-10360" />
            </div>

            <div data-id="element-10361">
              <label htmlFor="confirm-account" className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-10362">
                Confirm Account Number
              </label>
              <input id="confirm-account" type="text" inputMode="numeric" value={confirmAccountNumber} onChange={e => setConfirmAccountNumber(e.target.value.replace(/\D/g, ''))} placeholder="Re-enter account number" className={`w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 ${showMismatch ? 'border-red-400 focus:ring-red-300 focus:border-red-400' : 'border-gray-300 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]'}`} data-id="element-10363" />
              {showMismatch && <p className="mt-1.5 text-xs text-red-600" data-id="element-10364">
                  Account numbers do not match.
                </p>}
            </div>

            <p className="text-xs text-gray-500 pt-1" data-id="element-10365">
              The account name must exactly match your registered business name.
            </p>
          </div>
        </section>

        <section className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-10366">
          <h2 className="text-base font-semibold text-gray-900 mb-1" data-id="element-10367">
            Verification Document
          </h2>
          <p className="text-sm text-gray-500 mb-5" data-id="element-10368">
            Please upload a recent bank statement or a letter of confirmation
            from your bank.
          </p>

          {!file ? <div onClick={simulateUpload} onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={() => setIsDragging(false)} onDrop={onDrop} className={`border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-[var(--brand-primary)] bg-[var(--brand-primarylight)]' : 'border-gray-300 hover:border-gray-400 hover:bg-gray-50'}`} role="button" tabIndex={0} onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            simulateUpload();
          }
        }} data-id="element-10369">
              <UploadCloudIcon size={32} className="mx-auto text-gray-400 mb-3" data-id="element-10370" />
              <p className="text-sm font-medium text-gray-700" data-id="element-10371">
                Click to upload or drag and drop
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10372">
                PDF, JPG, or PNG up to 10MB
              </p>
            </div> : <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4 bg-gray-50" data-id="element-10373">
              <div className="flex items-center gap-3 min-w-0" data-id="element-10374">
                <div className="w-10 h-10 rounded-md bg-white border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-10375">
                  <FileIcon size={18} className="text-gray-500" data-id="element-10376" />
                </div>
                <div className="min-w-0" data-id="element-10377">
                  <p className="text-sm font-medium text-gray-900 truncate" data-id="element-10378">
                    {file.name}
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-10379">
                    {(file.size / 1024 / 1024).toFixed(2)} MB · Simulated upload
                  </p>
                </div>
              </div>
              <button onClick={() => setFile(null)} className="p-1.5 rounded-md hover:bg-gray-200 transition-colors flex-shrink-0" aria-label="Remove file" data-id="element-10380">
                <XIcon size={16} className="text-gray-500" data-id="element-10381" />
              </button>
            </div>}

          <input ref={fileInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" className="hidden" readOnly data-id="element-10382" />

          <p className="text-xs text-gray-500 mt-4" data-id="element-10383">
            Must clearly show the bank logo, your business name, and the new
            account number. Max size: 10MB (PDF, JPG, PNG).
          </p>
        </section>
      </div>

      <div className="fixed bottom-0 left-0 right-0 lg:left-[var(--sidebar-w,240px)] bg-white border-t border-gray-200 z-10" data-id="element-10384">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-10385">
          <button onClick={goBack} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-10386">
            Cancel
          </button>
          <button onClick={handleSubmit} disabled={!isValid || submitting} className="px-5 py-2.5 text-sm font-semibold rounded-md transition-colors disabled:cursor-not-allowed" style={{
          backgroundColor: isValid && !submitting ? 'var(--brand-primary)' : '#F3F4F6',
          color: isValid && !submitting ? '#ffffff' : '#9CA3AF'
        }} onMouseEnter={e => {
          if (isValid && !submitting) e.currentTarget.style.backgroundColor = 'var(--brand-primaryhover)';
        }} onMouseLeave={e => {
          if (isValid && !submitting) e.currentTarget.style.backgroundColor = 'var(--brand-primary)';
        }} data-id="element-10387">
            {submitting ? 'Submitting…' : 'Submit for Review'}
          </button>
        </div>
      </div>
    </div>;
}